import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import Logo from "./Logo";
import { site } from "../data/site";

/**
 * Footer — premium dark-navy layout.
 * Slightly darker navy than the final CTA above it for a subtle band step.
 */
const NAV_GROUPS = [
    {
        title: "Quick Links",
        links: [
            { label: "Home", href: "/" },
            { label: "About SRM", href: "/about" },
            { label: "Services", href: "/services" },
            { label: "Guardian Program", href: "/guardian-program" },
            { label: "Resources", href: "/resources" },
            { label: "Contact", href: "/contact" },
        ],
    },
    {
        title: "Core Services",
        links: [
            { label: "Assessment & Report", href: "/services" },
            { label: "Scope of Works & Tender", href: "/services" },
            { label: "Delivery Oversight", href: "/services" },
            { label: "Ongoing Guardianship & Monitoring", href: "/services" },
        ],
    },
    {
        title: "Legal",
        links: [
            { label: "Privacy", href: "/privacy" },
            { label: "Terms", href: "/terms" },
        ],
    },
];

export default function Footer() {
    const year = new Date().getFullYear();
    const { business } = site;

    return (
        <footer
            data-testid="footer"
            className="bg-srm-navyDark text-white pt-24 lg:pt-28 pb-10"
        >
            <div className="container-srm">
                {/* ── Top: brand + nav grid ───────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Brand block */}
                    <div className="lg:col-span-5">
                        <div data-testid="footer-logo" className="inline-block">
                            <Logo variant="light" size="lg" />
                        </div>
                        <p className="mt-8 text-white/65 leading-[1.7] max-w-md text-[14.5px]">
                            Independent Roof Asset Management for Strata Communities.
                            Practical advice. Defensible reporting. National coverage.
                        </p>

                        <ul className="mt-9 space-y-3.5 text-[14px]">
                            {business.phones?.map((p) => (
                                <li key={p.number} className="flex items-start gap-3.5 text-white/80">
                                    <Phone className="h-4 w-4 text-srm-gold mt-0.5" strokeWidth={1.8} />
                                    <div className="leading-tight">
                                        <a
                                            href={`tel:${p.number.replace(/\s+/g, "")}`}
                                            data-testid={`footer-phone-${p.number.replace(/\s+/g, "")}`}
                                            className="hover:text-white transition-colors font-medium"
                                        >
                                            {p.number}
                                        </a>
                                        <div className="text-white/55 text-[12px] mt-0.5">
                                            {p.name} · {p.role}
                                        </div>
                                    </div>
                                </li>
                            ))}
                            <li className="flex items-center gap-3.5 text-white/80">
                                <Mail className="h-4 w-4 text-srm-gold" strokeWidth={1.8} />
                                <a
                                    href={`mailto:${business.email}`}
                                    data-testid="footer-email"
                                    className="hover:text-white transition-colors break-all"
                                >
                                    {business.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3.5 text-white/80">
                                <MapPin
                                    className="h-4 w-4 text-srm-gold mt-0.5"
                                    strokeWidth={1.8}
                                />
                                <span>
                                    <Link
                                        to="/sunshine-coast-strata-roof-assessments"
                                        data-testid="footer-link-sunshine-coast"
                                        className="hover:text-white underline-offset-4 hover:underline transition-colors"
                                    >
                                        {business.office}
                                    </Link>{" "}
                                    · {business.serviceArea}
                                </span>
                            </li>
                        </ul>
                    </div>

                    {/* Nav columns */}
                    <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-10 lg:gap-10">
                        {NAV_GROUPS.map((group) => (
                            <div key={group.title}>
                                <div
                                    data-testid={`footer-col-${group.title.toLowerCase()}`}
                                    className="text-[11px] font-semibold tracking-[0.22em] uppercase text-srm-gold"
                                >
                                    {group.title}
                                </div>
                                <span
                                    aria-hidden
                                    className="mt-4 block h-px w-8 bg-white/15"
                                />
                                <ul className="mt-6 space-y-3.5">
                                    {group.links.map((l) => (
                                        <li key={l.label}>
                                            <Link
                                                to={l.href}
                                                data-testid={`footer-link-${l.label
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "-")
                                                    .replace(/[^a-z0-9-]/g, "")}`}
                                                className="text-white/70 hover:text-white text-[13.5px] leading-snug transition-colors"
                                            >
                                                {l.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Bottom: legal bar ───────────────────────────────── */}
                <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12.5px] text-white/55">
                    <div data-testid="footer-copyright">
                        © {year} {business.fullName}. All rights reserved.
                    </div>
                </div>
            </div>
        </footer>
    );
}
